import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  TransformComponent,
  TransformWrapper,
  type ReactZoomPanPinchRef,
} from "react-zoom-pan-pinch";
import { Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { OfflineImage } from "@/components/common/offline-image/offline-image";
import { PinDirectionCone } from "@/components/common/pin-direction-cone/pin-direction-cone";
import { PinMarkerDot } from "@/components/common/pin-marker-dot/pin-marker-dot";
import {
  PinDirectionHint,
  PinDirectionHintBanner,
} from "@/components/common/pin-direction-hint/pin-direction-hint";
import { bannerPlacement } from "@/components/common/pin-direction-hint/pin-direction-hint.helpers";
import {
  PinRotateHandle,
  PIN_ROTATE_HANDLE_CLASS,
} from "@/components/common/pin-rotate-handle/pin-rotate-handle";
import {
  TiledPlanViewer,
  type TiledOverlayItem,
} from "@/components/plans/tiled-plan-viewer/tiled-plan-viewer";
import { isTiledReady } from "@/components/plans/tiled-plan-viewer/tiled-plan-viewer.helpers";
import { useDragToRotate } from "@/hooks/use-drag-to-rotate";
import { planAssetKey } from "@/offline/asset-keys";
import { useOnlineStatus } from "@/offline/online-status";
import type { ProjectPlan } from "@/types/api";

interface ExpandedPlanViewProps {
  plan: ProjectPlan;
  /** Owner project — scopes cached tiles so unpin clears them. */
  projectId: string;
  pinX: number | null;
  pinY: number | null;
  onClose: () => void;
  isEditMode?: boolean;
  onPinChange?: (x: number, y: number) => void;
  /**
   * Plan-relative bearing in degrees `[0, 360)`, or null for an undirected pin
   * (see [[directional-pins]]). Drives the read-only cone in view mode and the
   * live cone + rotate handle in edit mode.
   */
  bearing: number | null;
  /**
   * Fired (continuously) with the snapped bearing as the user drags the rotate
   * handle. The parent writes it to its form state; "Done" only closes.
   */
  onBearingChange?: (bearing: number) => void;
}

const TAP_THRESHOLD_PX = 5;

/**
 * On-screen radius of the cone in px at `--pin-scale: 1` — matches
 * {@link PinDirectionCone}'s default so the rotate handle lands exactly on the
 * cone's tip on the invisible ring around the pin.
 */
const CONE_RADIUS_PX = 56;

/** Resting angle of the handle when no bearing is set yet — points to plan-top. */
const HANDLE_REST_BEARING = 0;

export function ExpandedPlanView({
  plan,
  projectId,
  pinX,
  pinY,
  onClose,
  isEditMode,
  onPinChange,
  bearing,
  onBearingChange,
}: ExpandedPlanViewProps) {
  const transformRef = useRef<ReactZoomPanPinchRef>(null);
  const pinContainerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  // The pin dot's DOM node — the rotate gesture's pin-centre anchor on BOTH
  // surfaces (its box centre IS the pin point — see [[pin-marker-dot.spec]]).
  // Rect-based and read lazily, so it stays correct under legacy pan/zoom AND
  // inside an OSD overlay portal, where there is no <img> rect to derive from.
  const pinDotRef = useRef<HTMLDivElement>(null);
  const pointerDownRef = useRef<{ x: number; y: number } | null>(null);
  const hasPin = pinX !== null && pinY !== null;
  // Direction is editable only when the surface is in edit mode AND a bearing
  // sink is wired. Gates the rotate handle and the at-rest preview cone, and
  // mirrors the rotate gesture's `enabled` (which also requires a pin).
  const canSetDirection = !!isEditMode && !!onBearingChange;

  // Drag-to-rotate is wired only when we can actually write a bearing AND a pin
  // exists (a bearing requires a pin — see [[directional-pins]]). The hook
  // stops propagation on every pointer event, so a rotate-drag never reaches
  // the container's tap/pan handler beneath and never drops/moves the pin.
  const { handleProps } = useDragToRotate({
    anchorRef: pinDotRef,
    onChange: (b) => onBearingChange?.(b),
    enabled: canSetDirection && hasPin,
  });
  const [naturalSize, setNaturalSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  const isOnline = useOnlineStatus();
  // Revisions whose OSD open failed this session (ready status but tiles
  // unreachable — FE-DEBT-007). Session-only; a new revision retries tiles.
  const [brokenTileRevisions, setBrokenTileRevisions] = useState<Set<string>>(
    () => new Set(),
  );
  // Online gate: offline parity rides the cached fallback JPEG through the
  // legacy OfflineImage path — tiles are online-first in v1 (spec §8).
  const useTiledViewer =
    isTiledReady(plan.latest_revision) &&
    isOnline &&
    !(plan.latest_revision && brokenTileRevisions.has(plan.latest_revision.id));

  // Pin layer for the tiled (OSD) surface — the same trio the legacy branch
  // renders (the view cone in both modes, the shared dot, the conditional
  // rotate handle), portalled into a zero-size point overlay. x/y = 0 because
  // the overlay host div IS the pin point. See [[tiled-plan-viewer.spec]].
  const pinOverlay = useMemo<TiledOverlayItem[]>(() => {
    if (pinX === null || pinY === null) return [];
    return [
      {
        id: "record-pin",
        x: pinX,
        y: pinY,
        render: () => (
          <>
            {/* View cone in BOTH modes — this fullscreen view shows a single
                pin (this record's), like the modal thumbnail, so the crowding
                that motivates the browse chevron (a multi-pin plan) doesn't
                apply. The faint at-rest preview is edit-only (you can only aim
                in edit mode); the rotate handle below is too. See
                [[directional-pins]]. */}
            <div className="pointer-events-none absolute" style={{ left: 0, top: 0 }}>
              <PinDirectionCone
                bearing={canSetDirection ? bearing ?? HANDLE_REST_BEARING : bearing}
                preview={canSetDirection && bearing == null}
              />
            </div>
            <PinMarkerDot ref={pinDotRef} xPct={0} yPct={0} />
            {canSetDirection && (
              <PinRotateHandle
                xPct={0}
                yPct={0}
                bearing={bearing}
                restBearing={HANDLE_REST_BEARING}
                radiusPx={CONE_RADIUS_PX}
                handleProps={handleProps}
                ariaLabel="Drag to set the direction the photo was taken"
              />
            )}
            {/* Transient finger coachmark — edit mode only, until a direction is
                dragged in. See [[pin-direction-hint.spec]]. */}
            {canSetDirection && bearing == null && (
              <PinDirectionHint
                key={`${pinX},${pinY}`}
                xPct={0}
                yPct={0}
                radiusPx={CONE_RADIUS_PX}
                restBearing={HANDLE_REST_BEARING}
              />
            )}
            {/* Helper text — anchored to the pin so the copy and the coachmark
                read together; placement keeps it on screen near any edge. See
                [[directional-pins]]. */}
            {canSetDirection && (
              <PinDirectionHintBanner
                xPct={0}
                yPct={0}
                radiusPx={CONE_RADIUS_PX}
                {...bannerPlacement(pinX ?? 0, pinY ?? 0)}
              />
            )}
          </>
        ),
      },
    ];
  }, [pinX, pinY, canSetDirection, bearing, handleProps]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  const measureImage = useCallback(() => {
    const img = imgRef.current;
    if (!img?.naturalWidth || !img?.naturalHeight) return;
    setNaturalSize((prev) =>
      prev &&
      prev.width === img.naturalWidth &&
      prev.height === img.naturalHeight
        ? prev
        : { width: img.naturalWidth, height: img.naturalHeight },
    );
  }, []);

  const screenToImageCoords = useCallback(
    (clientX: number, clientY: number) => {
      const img = imgRef.current;
      if (!img) return null;
      const rect = img.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return null;
      const x = (clientX - rect.left) / rect.width;
      const y = (clientY - rect.top) / rect.height;
      if (x < 0 || x > 1 || y < 0 || y > 1) return null;
      return { x, y };
    },
    [],
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!isEditMode || !e.isPrimary) return;
      pointerDownRef.current = { x: e.clientX, y: e.clientY };
    },
    [isEditMode],
  );

  const handlePointerCancel = useCallback(() => {
    pointerDownRef.current = null;
  }, []);

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (!isEditMode || !onPinChange || !e.isPrimary) return;
      const down = pointerDownRef.current;
      pointerDownRef.current = null;
      if (!down) return;
      const dx = Math.abs(e.clientX - down.x);
      const dy = Math.abs(e.clientY - down.y);
      if (dx >= TAP_THRESHOLD_PX || dy >= TAP_THRESHOLD_PX) return;
      const coords = screenToImageCoords(e.clientX, e.clientY);
      if (coords) onPinChange(coords.x, coords.y);
    },
    [isEditMode, onPinChange, screenToImageCoords],
  );

  const src = plan.latest_revision?.image_url ?? "";

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-gray-100">
      <div
        className="relative z-10 flex shrink-0 items-center justify-between p-4"
        style={{ paddingTop: "max(1rem, env(safe-area-inset-top, 1rem))" }}
      >
        <button
          type="button"
          onClick={onClose}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-black/10 text-gray-900 transition-colors hover:bg-black/20"
          aria-label="Close expanded plan"
        >
          <X className="h-5 w-5" />
        </button>
        <span className="absolute left-1/2 max-w-[60%] -translate-x-1/2 truncate text-sm font-medium text-gray-900">
          {plan.name}
        </span>
        <div className="w-10" />
      </div>
      <div className="relative min-h-0 flex-1 overflow-hidden">
        {useTiledViewer && plan.latest_revision?.tiles ? (
          /* Deep-zoom surface — selected only when the tile pipeline is ready
             AND we're online; every other state keeps the legacy path below
             byte-for-byte. Tap discrimination here is OSD's 10px/400ms (vs
             the legacy 5px pointer math) — see [[expanded-plan-view.spec]]. */
          <TiledPlanViewer
            tiles={plan.latest_revision.tiles}
            planId={plan.id}
            revisionId={plan.latest_revision.id}
            projectId={projectId}
            pinMode={!!isEditMode}
            onTap={(c) => {
              if (isEditMode && onPinChange) onPinChange(c.x, c.y);
            }}
            overlays={pinOverlay}
            onOpenFailed={() => {
              const revisionId = plan.latest_revision?.id;
              if (!revisionId) return;
              setBrokenTileRevisions((prev) => new Set(prev).add(revisionId));
            }}
          />
        ) : (
        <TransformWrapper
          ref={transformRef}
          initialScale={1}
          minScale={0.5}
          maxScale={5}
          centerOnInit
          wheel={{ step: 0.002 }}
          // The rotate handle opts out of panning here — the gesture's
          // stop-propagation does NOT stop a pan, because the library binds its
          // own native pointer listeners. See [[directional-pins]].
          panning={{ velocityDisabled: true, excluded: [PIN_ROTATE_HANDLE_CLASS] }}
          pinch={{ step: 5 }}
          onTransform={(ref) => {
            pinContainerRef.current?.style.setProperty(
              "--pin-scale",
              String(1 / ref.state.scale),
            );
          }}
        >
          <TransformComponent
            wrapperStyle={{ width: "100%", height: "100%" }}
            contentStyle={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              ref={pinContainerRef}
              className={`relative inline-block${isEditMode ? " cursor-crosshair" : ""}`}
              style={{
                ...(naturalSize && {
                  aspectRatio: `${naturalSize.width} / ${naturalSize.height}`,
                }),
                maxWidth: "100%",
                maxHeight: "100%",
                touchAction: "none",
              }}
              onPointerDown={handlePointerDown}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerCancel}
            >
              <OfflineImage
                ref={imgRef}
                assetKey={
                  plan.latest_revision
                    ? planAssetKey(
                        plan.id,
                        plan.latest_revision.id,
                        plan.latest_revision.tile_status,
                      )
                    : `plan:${plan.id}:none`
                }
                src={src}
                alt={plan.name}
                draggable={false}
                onLoad={measureImage}
                className="block select-none"
                style={{
                  width: "100%",
                  height: "100%",
                  userSelect: "none",
                  WebkitUserSelect: "none",
                  WebkitTouchCallout: "none",
                }}
              />
              {pinX !== null && pinY !== null && (
                <>
                  {/* View cone in BOTH modes — this fullscreen view shows a
                      single pin (this record's), like the modal thumbnail, so
                      the crowding that motivates the browse chevron (a multi-pin
                      plan) doesn't apply. In edit mode it adds a faint at-rest
                      preview (drawing-top) before any aim and tracks the handle
                      live once dragged; the preview never sets a bearing —
                      `pin_bearing` stays null until a real drag. See
                      [[directional-pins]]. */}
                  <div
                    className="pointer-events-none absolute"
                    style={{ left: `${pinX * 100}%`, top: `${pinY * 100}%` }}
                  >
                    <PinDirectionCone
                      bearing={canSetDirection ? bearing ?? HANDLE_REST_BEARING : bearing}
                      preview={canSetDirection && bearing == null}
                    />
                  </div>
                  {/* Pin marker — the shared round dot, identical to the
                      plan-viewer pin (not a map-location teardrop). Also the
                      rotate gesture's pin-centre anchor (box centre = pin
                      point). See [[pin-marker-dot.spec]] and
                      [[directional-pins]]. */}
                  <PinMarkerDot ref={pinDotRef} xPct={pinX} yPct={pinY} />
                  {/* Rotate handle — edit mode only; drag to set/adjust the
                      direction. Shared arrow-lever; pan/tap-isolated so it never
                      moves the pin or pans the plan. See [[pin-rotate-handle.spec]]. */}
                  {canSetDirection && (
                    <PinRotateHandle
                      xPct={pinX}
                      yPct={pinY}
                      bearing={bearing}
                      restBearing={HANDLE_REST_BEARING}
                      radiusPx={CONE_RADIUS_PX}
                      handleProps={handleProps}
                      ariaLabel="Drag to set the direction the photo was taken"
                    />
                  )}
                  {/* Transient finger coachmark — edit mode only, until a
                      direction is dragged in. See [[pin-direction-hint.spec]]. */}
                  {canSetDirection && bearing == null && (
                    <PinDirectionHint
                      key={`${pinX},${pinY}`}
                      xPct={pinX}
                      yPct={pinY}
                      radiusPx={CONE_RADIUS_PX}
                      restBearing={HANDLE_REST_BEARING}
                    />
                  )}
                  {/* Helper text — anchored to the pin so the copy and the
                      coachmark read together; placement keeps it on screen near
                      any edge. See [[directional-pins]]. */}
                  {canSetDirection && (
                    <PinDirectionHintBanner
                      xPct={pinX}
                      yPct={pinY}
                      radiusPx={CONE_RADIUS_PX}
                      {...bannerPlacement(pinX, pinY)}
                    />
                  )}
                </>
              )}
            </div>
          </TransformComponent>
        </TransformWrapper>
        )}
      </div>
      {/* Done — the primary way out of the editor. The "×" is still there and
          still works, but it reads as "discard" to a user who has just dropped
          a pin, and it sits in the corner furthest from the thumb that placed
          it. Edit mode only: in view mode there is nothing to finish. Nothing
          is saved here — the pin and bearing are already in the parent's form
          state — so this closes, exactly like the "×". */}
      {isEditMode && (
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 z-20 flex justify-center p-4"
          style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom, 1rem))" }}
        >
          <Button
            type="button"
            size="lg"
            onClick={onClose}
            className="pointer-events-auto rounded-full px-8 shadow-lg"
          >
            <Check className="h-4 w-4" />
            Done
          </Button>
        </div>
      )}
    </div>
  );
}
